import { SelectorMatchInfo } from '@core/types';
import { STABLE_DATA_ATTRIBUTES, MAX_HIGHLIGHTED_ELEMENTS } from '@shared/constants';
import { cssEscape, cssEscapeAttr } from '@shared/utils/dom';

export class SelectorEngine {
  private cache = new Map<string, SelectorMatchInfo>();

  generate(element: Element): string {
    if (!(element instanceof Element)) return '';

    const tag = element.tagName.toLowerCase();
    if (tag === 'html' || tag === 'body') return tag;

    // 1) Unique ID
    if (element.id) {
      const idSel = '#' + cssEscape(element.id);
      if (this.isUnique(idSel, element)) return idSel;
    }

    // 2) Stable data attributes
    for (const attr of STABLE_DATA_ATTRIBUTES) {
      const value = element.getAttribute(attr);
      if (value) {
        const sel = `[${attr}="${cssEscapeAttr(value)}"]`;
        if (this.isUnique(sel, element)) return sel;
      }
    }

    // 3) Path with limited classes + nth-of-type
    const parts: string[] = [];
    let node: Element | null = element;

    while (node && node.nodeType === 1 && parts.length < 7 && node !== document.documentElement) {
      let part = node.tagName.toLowerCase();

      const classes = (node.getAttribute('class') || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter((c) => !c.startsWith('ce-') && !c.startsWith('__campaign'))
        .slice(0, 3);

      if (classes.length) {
        part += '.' + classes.map(cssEscape).join('.');
      }

      const parent = node.parentElement;
      if (parent) {
        const sameTagSiblings = Array.from(parent.children).filter(
          (child) => child.tagName === node!.tagName,
        );
        if (sameTagSiblings.length > 1) {
          const index = sameTagSiblings.indexOf(node!);
          part += `:nth-of-type(${index + 1})`;
        }
      }

      parts.unshift(part);

      const sel = parts.join(' > ');
      if (this.isUnique(sel, element)) return sel;

      node = node.parentElement;
    }

    return parts.join(' > ');
  }

  validate(selector: string): boolean {
    try {
      document.querySelector(selector);
      return true;
    } catch {
      return false;
    }
  }

  getMatchInfo(selector: string): SelectorMatchInfo {
    if (this.cache.has(selector)) {
      return this.cache.get(selector)!;
    }

    let count = 0;
    try {
      count = document.querySelectorAll(selector).length;
    } catch {
      count = 0;
    }

    const info = { ok: count > 0, count };
    this.cache.set(selector, info);
    return info;
  }

  private isUnique(selector: string, element: Element): boolean {
    try {
      const found = document.querySelectorAll(selector);
      return found.length === 1 && found[0] === element;
    } catch {
      return false;
    }
  }

  clearCache(): void {
    this.cache.clear();
  }

  getHighlightedElements(selector: string): Element[] {
    const elements = Array.from(document.querySelectorAll(selector));
    return elements.slice(0, MAX_HIGHLIGHTED_ELEMENTS);
  }
}
